<script setup lang="ts">
import type { CmsElementImageGallery } from "@shopware/composables";
import { useCmsElementConfig } from "#imports";
import { isSpatial } from "@shopware/cms-base-layer/helpers/media/isSpatial";
import SwSlider from "@shopware/cms-base-layer/components/SwSlider.vue";
import { computed, onMounted, ref, useTemplateRef } from "vue";

const props = withDefaults(
  defineProps<{
    content: CmsElementImageGallery;
    slidesToShow?: number;
    slidesToScroll?: number;
  }>(),
  {
    slidesToShow: 5,
    slidesToScroll: 1,
  }
);

const { getConfigValue } = useCmsElementConfig(props.content);

const speed = ref<number>(300);
const currentIndex = ref(0);
const currentThumb = ref(0);
const imageSlider = useTemplateRef("imageSlider");
const imageThumbsTrack = useTemplateRef("imageThumbsTrack");
const isLoading = ref(true);
const imageThumbsTrackStyle = ref({});
const imageThumbs = useTemplateRef("imageThumbs");
const imageThumbsStyle = ref({});
const mediaGalleryImages = computed(
  () => props.content.data?.sliderItems ?? []
);
const { product } = useProduct();
const mediaGallery = computed(() => {
  const combinedMedia =
    product.value.extensions?.solidPvCombinedMedia?.combinedMedia;
  const sliderItems = props.content.data?.sliderItems;
  return combinedMedia ?? sliderItems ?? [];
});

const slidesToShowNo = ref(props.slidesToShow);

const updateslidesToShowNo = () => {
  const width = window.innerWidth;
  if (width < 768) {
    slidesToShowNo.value = 2; // Small tablets
  } else if (width < 1024) {
    slidesToShowNo.value = 3; // Large tablets
  } else if (width < 1280) {
    slidesToShowNo.value = 4; // Small desktops
  } else {
    slidesToShowNo.value = props.slidesToShow; // Default
  }
};

const getYoutubeThumbnailUrl = (image: any) => {
  if (image) {
    return `https://img.youtube.com/vi/${image.videoId}/0.jpg`;
  }
};

const galleryPosition = computed<string>(
  () => getConfigValue("galleryPosition") ?? "left"
);
const scrollPx = ref(0);

onMounted(() => {
  initThumbs();
  updateslidesToShowNo();
});

function initThumbs() {
  if (imageThumbsTrack.value) {
    setTimeout(() => {
      if (galleryPosition.value === "left") {
        const clientHeight = imageThumbsTrack.value?.clientHeight ?? 0;
        scrollPx.value = clientHeight / mediaGallery.value.length;
        imageThumbsStyle.value = {
          height: `${scrollPx.value * +slidesToShowNo.value}px`,
        };
      } else {
        const thumbWidth = 80; // w-20 = 5rem = 80px
        const gap = 8; // Assuming 2px padding on each side (px-1)
        scrollPx.value = thumbWidth + gap;

        if (mediaGallery.value.length < slidesToShowNo.value) {
          imageThumbsStyle.value = {
            width: `${scrollPx.value * slidesToShowNo.value}px`,
          };
        } else {
          imageThumbsTrackStyle.value = {
            width: `${scrollPx.value * mediaGallery.value.length}px`,
          };
        }
      }
      isLoading.value = false;
    }, 100);
  }
}

function changeCover(i: number) {
  if (i === currentIndex.value) return;
  currentIndex.value > i ? move("previous", i) : move("next", i);
  imageSlider.value?.goToSlide(i);
}

function handleChangeSlide(e: number) {
  currentIndex.value = e;
  if (currentIndex.value > currentThumb.value + slidesToShowNo.value - 1) {
    move("next", currentIndex.value);
    return;
  }
  if (currentIndex.value < currentThumb.value) {
    move("previous", currentIndex.value);
    return;
  }
}

function move(type: "next" | "previous", specificIndex?: number | string) {
  let step: number;
  const index =
    typeof specificIndex !== "number"
      ? Number.parseInt(specificIndex as string)
      : specificIndex;

  if (index >= 0) {
    if (type === "next") {
      step = Math.min(index, mediaGallery.value.length - slidesToShowNo.value);
    } else {
      step = Math.max(index - props.slidesToScroll, 0);
    }
  } else {
    if (type === "next") {
      step = Math.min(
        currentThumb.value + props.slidesToScroll,
        mediaGallery.value.length - slidesToShowNo.value
      );
    } else {
      step = Math.max(currentThumb.value - props.slidesToScroll, 0);
    }
  }

  currentThumb.value = step;
  let xAxis = 0;
  let yAxis = 0;

  if (galleryPosition.value === "left") {
    yAxis = scrollPx.value * currentThumb.value;
  } else {
    xAxis = scrollPx.value * currentThumb.value;
  }

  imageThumbsTrackStyle.value = {
    ...imageThumbsTrackStyle.value,
    transform: `translate3d(-${xAxis}px, -${yAxis}px, 0px)`,
    transition: `transform ${speed.value}ms ease 0s`,
  };
}

function previous() {
  if (currentThumb.value <= 0) {
    return;
  }
  move("previous");
  imageSlider.value?.goToSlide(currentThumb.value);
}

function next() {
  if (currentThumb.value >= mediaGallery.value.length) {
    return;
  }
  move("next");
  imageSlider.value?.goToSlide(currentThumb.value);
}
</script>

<template>
  <div
    :class="{
      'opacity-0': isLoading,
      'flex gap-10': true,
      'flex-col-reverse': galleryPosition === 'underneath',
    }"
  >
    <div
      :class="{
        'hidden lg:flex basis-20 relative flex-col items-center':
          galleryPosition === 'left',
        'flex relative w-full': galleryPosition === 'underneath',
      }"
    >
      <button
        v-if="mediaGallery.length >= slidesToShowNo"
        class="disabled:opacity-10 p-4 h-12 w-12 mt-2 rounded-3xl bg-skin-icon flex"
        :class="{
          'disabled opacity-50 pointer-events-none cursor-not-allowed':
            currentIndex < 1 || mediaGallery.length < slidesToShowNo,
        }"
        aria-label="Previous image"
        @click="previous"
      >
        <svg
          width="24"
          height="16"
          viewBox="0 0 24 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1.23039 8.70711C0.839869 8.31658 0.839869 7.68342 1.23039 7.29289L7.59435 0.928932C7.98488 0.538408 8.61804 0.538408 9.00857 0.928932C9.39909 1.31946 9.39909 1.95262 9.00857 2.34315L3.35171 8L9.00857 13.6569C9.39909 14.0474 9.39909 14.6805 9.00857 15.0711C8.61804 15.4616 7.98488 15.4616 7.59435 15.0711L1.23039 8.70711ZM23.9375 9H1.9375V7H23.9375V9Z"
            fill="#4A4A49"
          />
        </svg>
      </button>
      <div
        ref="imageThumbs"
        class="overflow-hidden -mt-2.5 w-full"
        :style="imageThumbsStyle"
      >
        <div
          ref="imageThumbsTrack"
          :class="{
            flex: true,
            'flex-col': galleryPosition === 'left',
          }"
          :style="imageThumbsTrackStyle"
        >
          <div
            v-for="(image, i) in mediaGallery"
            :key="image.id"
            :class="{
              'py-2.5': galleryPosition === 'left',
              'flex px-1': galleryPosition === 'underneath',
            }"
          >
            <div
              class="w-20 h-20 overflow-hidden cursor-pointer p-1 border-secondary-200 rounded transition duration-150 ease-in-out border-2"
              :class="
                i === currentIndex
                  ? 'border-skin-theme'
                  : 'border-[transparent]'
              "
              @click="() => changeCover(i)"
            >
              <template v-if="image.media">
                <div v-if="isSpatial(image.media)" class="h-full relative">
                  <CmsElementImageGallery3dPlaceholder
                    class="w-full h-full object-center"
                  />
                  <span
                    class="absolute bottom-0 text-sm bg-gray rounded px-1 text-white"
                  >
                    3D</span
                  >
                </div>
                <NuxtImg
                  v-else
                  loading="lazy"
                  :src="image.media.url"
                  class="m-auto h-full object-center object-cover max-h-full"
                  alt="Product image"
                />
              </template>
              <template v-else-if="image.source == 'youtube'">
                <NuxtImg
                  loading="lazy"
                  :src="
                    image.thumbnailMedia?.url ?? getYoutubeThumbnailUrl(image)
                  "
                  class="m-auto h-full object-center object-cover max-h-full"
                  alt="Product image"
                />
              </template>
            </div>
          </div>
        </div>
      </div>
      <button
        v-if="mediaGallery.length > 1"
        class="disabled:opacity-10 p-4 h-12 w-12 mt-2 rounded-3xl bg-skin-icon flex"
        :class="{
          'disabled opacity-50 pointer-events-none cursor-not-allowed':
            currentIndex >= mediaGallery.length - slidesToShowNo,
        }"
        aria-label="Next image"
        @click="next"
      >
        <svg
          width="24"
          height="16"
          viewBox="0 0 24 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M22.7696 8.70711C23.1601 8.31658 23.1601 7.68342 22.7696 7.29289L16.4056 0.928932C16.0151 0.538408 15.382 0.538408 14.9914 0.928932C14.6009 1.31946 14.6009 1.95262 14.9914 2.34315L20.6483 8L14.9914 13.6569C14.6009 14.0474 14.6009 14.6805 14.9914 15.0711C15.382 15.4616 16.0151 15.4616 16.4056 15.0711L22.7696 8.70711ZM0.0625 9H22.0625V7H0.0625V9Z"
            fill="#4A4A49"
          />
        </svg>
      </button>
    </div>

    <div class="flex-1 overflow-hidden gallery-slider-block">
      <SwSlider
        ref="imageSlider"
        :config="props.content.config"
        @change-slide="handleChangeSlide"
      >
        <template v-for="image of mediaGallery">
          <ClientOnly>
            <CmsElementImage
              v-if="image.media"
              :key="image.id"
              class="h-[20rem] md:h-[28.125rem] bg-white rounded-sm max-h-full"
              :image-gallery="true"
              :content="{ data: image, config: props.content.config } as any"
            />
            <ProductDetailYoutubeVideo v-else :image="image" />
          </ClientOnly>
        </template>
      </SwSlider>
    </div>
  </div>
</template>
